import { Button, Heading } from "@r360/library";
import classNames from "classnames";
import React from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { getNewOrModifiedCartProducts, hasBookstat, isAccommodation } from "../../BusinessUtils";
import { productTypeNames } from "../../Constants";
import { formatPrice, getCartItemPrice, getFancyAccomodationPrice, getFancyCartItemPrice } from "../../Helper";
import useAppSelector from "../../hooks/useAppSelector";
import useTranslate from "../../hooks/useTranslate";
import { checkoutUrlSelector } from "../../Selectors";
import { agesSelector, currencySelector } from "../../selectors/Selectors";
import { TCartItem, TGeneralAges } from "../../store/types";
import ArrowRight from "../UI/R360Icons/ArrowRight";

type TCartDropdown = {
    onClose: () => void;
    cartProducts: TCartItem[];
    cartButtonId: string;
    id: string;
};

const CartDropdown = ({ onClose, cartProducts, cartButtonId, id }: TCartDropdown) => {
    const t = useTranslate();
    const location = useLocation();
    const currency = useAppSelector(currencySelector);
    const generalAges: TGeneralAges = useAppSelector(agesSelector);
    const checkoutUrl = useAppSelector(checkoutUrlSelector);

    let totalPrice = 0;

    const newOrModifiedCartProducts = getNewOrModifiedCartProducts(cartProducts);
    const newOrModifiedAccommodationCartProducts = newOrModifiedCartProducts.filter(isAccommodation);

    // Get all extras for accommodations in cart.
    const accommodationExtrasToSum = newOrModifiedAccommodationCartProducts
        .flatMap((accommodation: TCartItem) => accommodation.info?.extras || [])
        .filter((extra: TCartItem) => !hasBookstat(extra) || extra.additional);

    // Add values to variables
    newOrModifiedCartProducts.forEach((item: TCartItem) => {
        totalPrice += getCartItemPrice(item, generalAges) ?? 0;
    });

    // Add costs for accommodation extras
    const accommodationExtrasToSumFiltered = accommodationExtrasToSum.filter((extra: TCartItem) => {
        if (extra.rules.mandatory_web) {
            return false;
        } else {
            return (extra.quantity !== 0 && extra.rules.max_qty !== 0) || extra.checked === true;
        }
    });

    accommodationExtrasToSumFiltered.forEach((item: TCartItem) => {
        totalPrice += item.additional?.price || item.price;
    });

    const cartLink = location.pathname === "/cart" ? checkoutUrl : "/cart";

    const onToCartClick = () => {
        onClose();
    };

    const getExtrasPrice = (item: TCartItem) => {
        const extras = item.info.extras.filter(extra => extra.quantity > 0);
        const extrasPrice = extras?.reduce((prev, curr) => {
            if (curr.rules.mandatory_web) {
                return prev * curr.quantity;
            }

            return prev + (curr.additional?.price ?? curr.price);
        }, 0);
        return extrasPrice;
    };

    const connectedProductPrice = (item: TCartItem) => {
        const connectedProducts = cartProducts.filter(cartItem => cartItem.connectedProductId === item.cartItemId);

        return connectedProducts.reduce((sum, product) => {
            if (product) {
                return sum + product.aprice * product.count;
            }
            return sum;
        }, 0);
    };

    return (
        <div
            id={id}
            aria-labelledby={cartButtonId}
            className={classNames("main-header__cart-dropdown", {
                "main-header__cart-dropdown--loginPage": location.pathname.includes("login"),
            })}
        >
            <div className="main-header__cart-dropdown-header">
                <Heading type={"h6"}>{t("book.cart.title")}</Heading>
            </div>
            <div
                className={classNames("main-header__cart-dropdown-content", {
                    "main-header__cart-dropdown-last": cartProducts.length > 4,
                })}
            >
                {cartProducts
                    .filter(cartItem => !cartItem.connectedProductId)
                    .map((cartItem: TCartItem, index: number) => (
                        <Row key={index} className="main-header__cart-dropdown-row">
                            <Col md={12} className="u-d-flex u-justify-content-between">
                                <div>
                                    <span className="u-fw-medium u-mr-12">
                                        {cartItem.groupTitle || cartItem.pooldesc4}
                                    </span>
                                </div>
                                <div style={{ minWidth: "100px" }}>
                                    {!cartItem.detlid && (
                                        <span style={{ float: "right" }}>
                                            {cartItem.kind === productTypeNames.ACCOMMODATION
                                                ? getFancyAccomodationPrice(
                                                      cartItem,
                                                      getExtrasPrice(cartItem) + connectedProductPrice(cartItem),
                                                      currency
                                                  )
                                                : getFancyCartItemPrice(cartItem, currency, generalAges)}
                                        </span>
                                    )}
                                </div>
                            </Col>
                            <Col>
                                <span className="u-fs-12">{`${
                                    cartItem.kind === productTypeNames.ACCOMMODATION ||
                                    cartItem.kind === productTypeNames.PACKAGE
                                        ? cartItem.info.guests.totalGuests
                                        : cartItem.count
                                        ? cartItem.count
                                        : cartItem.quantity
                                } ${
                                    cartItem.count > 1 ||
                                    cartItem.quantity > 1 ||
                                    (cartItem.info?.guests?.totalGuests && cartItem.info.guests.totalGuests > 1)
                                        ? t("book.general.guests")
                                        : t("book.general.guest")
                                }`}</span>
                            </Col>
                        </Row>
                    ))}
            </div>
            {cartProducts.length > 4 && <div className="main-header__dropdown-footer-gradient"></div>}
            <div className="main-header__cart-dropdown-footer">
                <p className="u-fw-medium">{t("book.general.total")}</p>
                <p className="u-fw-medium">{formatPrice(totalPrice, currency)}</p>
            </div>
            <Button
                link
                linkTo={cartLink}
                onClick={onToCartClick}
                fullWidth
                type="secondary"
                rightIcon={<ArrowRight />}
            >
                {location.pathname === "/cart" ? t("book.cart.summary.goto_checkout") : t("book.general.go_to_cart")}
            </Button>
        </div>
    );
};

export default CartDropdown;
